"use client";

import { motion } from "framer-motion";
import { useCallback, useId, useState } from "react";

import { hasLongHeadingWord, railHeadingSize } from "@/lib/rail-heading";
import { cn } from "@/lib/utils";
import type { QuickAnswersSectionData } from "@/sanity/types";

interface QuickAnswersProps {
  data: QuickAnswersSectionData;
}

/**
 * QuickAnswers — "Quick answers".
 * Two-column layout: left H2 (dark line + accent line), right a numbered
 * accordion of Q&A. Panels expand/collapse with a smooth Framer Motion
 * height+opacity animation (matching the Services accordion). WAI-ARIA
 * disclosure pattern with +→× toggle, arrow-key navigation, and ESC to close.
 */
export function QuickAnswers({ data }: QuickAnswersProps) {
  const items = data.items ?? [];
  const [openIndex, setOpenIndex] = useState<number | null>(
    items.length > 0 ? 0 : null,
  );
  const id = useId();

  const toggle = useCallback((index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (event.key === "Escape") {
        setOpenIndex(null);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        const next = index + 1 < items.length ? index + 1 : 0;
        document.getElementById(`${id}-trigger-${next}`)?.focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const prev = index - 1 >= 0 ? index - 1 : items.length - 1;
        document.getElementById(`${id}-trigger-${prev}`)?.focus();
      } else if (event.key === "Home") {
        event.preventDefault();
        document.getElementById(`${id}-trigger-0`)?.focus();
      } else if (event.key === "End") {
        event.preventDefault();
        document.getElementById(`${id}-trigger-${items.length - 1}`)?.focus();
      }
    },
    [id, items.length],
  );

  if (items.length === 0) return null;

  return (
    <section
      aria-labelledby="quick-answers-heading"
      data-section-id="quick_answers"
      className="bg-foreground py-16 md:py-20 lg:py-24 xl:py-28"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="flex flex-col gap-12 lg:grid lg:grid-cols-[minmax(320px,420px)_1fr] lg:items-start lg:gap-16 xl:gap-24">
          <h2
            id="quick-answers-heading"
            className={cn(
              "font-light tracking-[-0.02em]",
              railHeadingSize(
                "xl98",
                // "Quick answers on Shopify development" overspilled the rail
                // by 132px, across the accordion. See rail-heading.ts.
                hasLongHeadingWord(data.headingText, data.headingAccent),
              ),
            )}
          >
            <span className="block text-[#121212]">{data.headingText}</span>{" "}
            <span className="block text-accent-ink">{data.headingAccent}</span>
          </h2>

          <ul role="list" className="border-t border-[#cbcbcb]">
            {items.map((item, index) => {
              const open = openIndex === index;
              const triggerId = `${id}-trigger-${index}`;
              const panelId = `${id}-panel-${index}`;
              const indexLabel = `${String(index + 1).padStart(2, "0")}/`;
              return (
                <li key={index} className="border-b border-[#cbcbcb]">
                  <h3>
                    <button
                      id={triggerId}
                      type="button"
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => toggle(index)}
                      onKeyDown={(event) => onKeyDown(event, index)}
                      data-analytics-cta={`quick_answers_${index + 1}`}
                      data-analytics-location="quick_answers"
                      className="flex w-full cursor-pointer items-start justify-between gap-6 py-7 text-left transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 lg:py-8"
                    >
                      <span className="flex-1 font-light leading-[1.1] tracking-[-0.02em] text-[#121212] text-[20px] md:text-[26px] lg:text-[30px] xl:text-[34px]">
                        <span>{indexLabel}</span>
                        <span>{item.question}</span>
                      </span>
                      <motion.span
                        aria-hidden="true"
                        animate={{ rotate: open ? 45 : 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="flex size-7 shrink-0 items-center justify-center lg:size-8"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#4dd017"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          className="size-6"
                        >
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </motion.span>
                    </button>
                  </h3>
                  <motion.div
                    id={panelId}
                    role="region"
                    // Question + section heading: service pages repeat these
                    // questions in their FAQ, and two regions with the same
                    // name fail axe landmark-unique.
                    aria-labelledby={`${triggerId} quick-answers-heading`}
                    aria-hidden={!open}
                    inert={!open}
                    initial={false}
                    animate={{
                      height: open ? "auto" : 0,
                      opacity: open ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="max-w-[760px] pb-7 text-[15px] font-light leading-[1.36] text-[#3d3d3d] md:text-[16px] lg:pb-8 lg:text-[18px]">
                      {item.answer}
                    </p>
                  </motion.div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
